import { getCPDataWithoutParams } from 'src/clinic/services'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { IDB_STORES } from 'src/consumer/constants'
import {
  getAllRecordsFromStore,
  putRecordsInStore,
  searchStoreByTitleFast,
} from 'src/emr/database/indexedDbDao'

/**
 * Local-first data for the patient form.
 *
 * Cities: the platform city database for a country is pulled once (pages of 500) into the
 * `meta_cities` IndexedDB store, so the City field searches locally and instantly (and works
 * offline). Until the store is warm, searches fall back to the API.
 *
 * Tag suggestions: the brand's tag library is cached per brand in `patient_tag_suggestions`
 * so chips appear immediately; the API refresh runs in the background and upserts.
 */

const CITY_SEARCH_API = '/api/secure/location/city/search'
const CITY_PAGE_SIZE = 500
const CITY_MAX_PAGES = 40
const CITY_STORE = IDB_STORES.META_CITIES.name
const TAG_STORE = IDB_STORES.PATIENT_TAG_SUGGESTIONS.name
const CITY_CACHE_KEY_PREFIX = 'metaCitiesLoadedInIdb:'
const CITY_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000

const inflightCityLoads = new Map()

function normalizeIso(iso) {
  return typeof iso === 'string' && /^[A-Za-z]{2}$/.test(iso.trim()) ? iso.trim().toUpperCase() : ''
}

export function getCityLabel(city) {
  if (!city) return ''
  return city.title || city.name || city.label || ''
}

export function getCityStateLabel(city) {
  return city?.administrativeEntityName || city?.state || ''
}

function toCityRecord(city, iso) {
  const title = getCityLabel(city)
  if (!title) return null
  const countryCode = normalizeIso(city?.countryCode) || iso
  return {
    ...city,
    id: city?.id || `${countryCode}:${title}:${getCityStateLabel(city)}`.toLowerCase(),
    title,
    countryCode,
    lowerCaseIndexField: title.toLowerCase(),
  }
}

function citiesLoadedAt(iso) {
  try {
    const raw = localStorage.getItem(`${CITY_CACHE_KEY_PREFIX}${iso}`)
    return raw ? Number(raw) : 0
  } catch (error) {
    return 0
  }
}

function markCitiesLoaded(iso) {
  try {
    localStorage.setItem(`${CITY_CACHE_KEY_PREFIX}${iso}`, `${Date.now()}`)
  } catch (error) {
    // storage unavailable — the store still holds the rows for this session
  }
}

export function areCitiesCached(iso) {
  const normalized = normalizeIso(iso)
  if (!normalized) return false
  const loadedAt = citiesLoadedAt(normalized)
  return loadedAt > 0 && Date.now() - loadedAt < CITY_CACHE_TTL_MS
}

async function fetchCityPage({ search = '', page = 1, pageSize = CITY_PAGE_SIZE, iso }) {
  const params = new URLSearchParams({ search, page: String(page), pageSize: String(pageSize) })
  if (iso) params.set('countryCode', iso.toLowerCase())
  const response = await getCPDataWithoutParams(`${CITY_SEARCH_API}?${params.toString()}`)
  if (response?.status !== 200) return []
  const content = response?.data?.content
  return Array.isArray(content) ? content : []
}

/** Pull every city of a country into IndexedDB once (idempotent, de-duplicated across callers). */
export async function ensureCitiesCached(iso) {
  const normalized = normalizeIso(iso)
  if (!normalized || areCitiesCached(normalized)) return true
  if (inflightCityLoads.has(normalized)) return inflightCityLoads.get(normalized)

  const task = (async () => {
    try {
      for (let page = 1; page <= CITY_MAX_PAGES; page += 1) {
        const rows = await fetchCityPage({ page, iso: normalized })
        const records = rows.map((row) => toCityRecord(row, normalized)).filter(Boolean)
        if (records.length > 0) {
          await putRecordsInStore(CITY_STORE, records)
        }
        if (rows.length < CITY_PAGE_SIZE) break
      }
      markCitiesLoaded(normalized)
      return true
    } catch (error) {
      return false
    } finally {
      inflightCityLoads.delete(normalized)
    }
  })()
  inflightCityLoads.set(normalized, task)
  return task
}

/** The city database holds a few towns twice (with and without a state); show each once. */
function dedupeCities(rows) {
  const byTitle = new Map()
  rows.forEach((row) => {
    const key = `${row.lowerCaseIndexField || getCityLabel(row).toLowerCase()}|${row.countryCode || ''}`
    const existing = byTitle.get(key)
    if (!existing || (!getCityStateLabel(existing) && getCityStateLabel(row))) {
      byTitle.set(key, row)
    }
  })
  return rows.filter((row) => {
    const key = `${row.lowerCaseIndexField || getCityLabel(row).toLowerCase()}|${row.countryCode || ''}`
    return byTitle.get(key) === row
  })
}

function rankCity(record, needle) {
  const title = record.lowerCaseIndexField || ''
  if (title === needle) return 0
  if (title.startsWith(needle)) return 1
  return 2
}

/**
 * Search cities: local store first (prefix, then contains, then related names), API fallback
 * while the store is cold. Always returns at most `limit` rows, best matches first.
 */
export async function searchCities(term, { iso, limit = 8 } = {}) {
  const needle = String(term || '').trim().toLowerCase()
  const normalized = normalizeIso(iso)
  if (!needle) return []

  let local = []
  try {
    local = await searchStoreByTitleFast(CITY_STORE, needle, { limit: limit * 4 })
    if (local.length < limit && needle.length >= 3) {
      const all = await getAllRecordsFromStore(CITY_STORE)
      const seen = new Set(local.map((row) => row.id))
      all.forEach((row) => {
        if (
          !seen.has(row.id) &&
          Array.isArray(row.relatedNames) &&
          row.relatedNames.some((name) => String(name).toLowerCase().includes(needle))
        ) {
          local.push(row)
        }
      })
    }
  } catch (error) {
    local = []
  }
  if (normalized) {
    local = local.filter((row) => !row.countryCode || row.countryCode === normalized)
  }
  local.sort((a, b) => rankCity(a, needle) - rankCity(b, needle) || (b.population || 0) - (a.population || 0))
  local = dedupeCities(local)

  if (local.length > 0 || areCitiesCached(normalized)) {
    return local.slice(0, limit)
  }

  // Cold store: ask the API for this term and warm the store for next time.
  ensureCitiesCached(normalized)
  try {
    const rows = await fetchCityPage({ search: needle, page: 1, pageSize: limit * 2, iso: normalized })
    const records = rows.map((row) => toCityRecord(row, normalized)).filter(Boolean)
    if (records.length > 0) putRecordsInStore(CITY_STORE, records).catch(() => {})
    records.sort((a, b) => rankCity(a, needle) - rankCity(b, needle))
    return dedupeCities(records).slice(0, limit)
  } catch (error) {
    return []
  }
}

// ───────────────────────── tag suggestions ─────────────────────────

function tagKey(tag) {
  return (tag?.tagCode || tag?.label || '').toString().trim().toLowerCase()
}

function toTagRecord(tag, brandId) {
  const key = tagKey(tag)
  if (!key || !tag?.label) return null
  return {
    ...tag,
    id: `${brandId || 'any'}:${key}`,
    clinicBrandId: brandId || 'any',
    lowerCaseIndexField: tag.label.toLowerCase(),
    cachedAt: Date.now(),
  }
}

export async function getCachedTagSuggestions(brandId) {
  try {
    const all = await getAllRecordsFromStore(TAG_STORE)
    const scope = brandId || 'any'
    return all
      .filter((row) => row.clinicBrandId === scope)
      .sort((a, b) => (b.lastUsedTime || b.cachedAt || 0) > (a.lastUsedTime || a.cachedAt || 0) ? 1 : -1)
  } catch (error) {
    return []
  }
}

export async function cacheTagSuggestions(tags, brandId) {
  const records = (tags || []).map((tag) => toTagRecord(tag, brandId)).filter(Boolean)
  if (records.length === 0) return
  try {
    await putRecordsInStore(TAG_STORE, records)
  } catch (error) {
    // cache only
  }
}

/** Fetch the brand's suggestions from the portal's tags API and refresh the cache. */
export async function refreshTagSuggestions(patientTagsApiSignature, brandId) {
  if (!patientTagsApiSignature) return []
  const params = new URLSearchParams({ page: '1', pageSize: '50' })
  if (brandId) params.set('clinicBrandId', brandId)
  const url = `${patientTagsApiSignature}/suggestions?${params.toString()}`
  try {
    const response = patientTagsApiSignature.includes('/support/')
      ? await getSPDataWithParams(url, {})
      : await getCPDataWithoutParams(url)
    const content = response?.data?.data?.content || response?.data?.content || []
    const tags = Array.isArray(content) ? content : []
    await cacheTagSuggestions(tags, brandId)
    return tags
  } catch (error) {
    return []
  }
}
